import { showToast } from './toast.js';
import { getCurrentUser, getToken, handleAuthError, isUserSignedIn } from './auth.js';
import { CONFIG } from '../config.js';
import { getCurrentTenant } from '../tenant.js';

let allApps = [];
let currentAppFilters = createEmptyCurriculumFilters();
let currentCurriculumLookups = createEmptyCurriculumLookups();
let currentPage = 1;
let currentTotal = 0;
let autoEducationFiltersApplied = false;
const APPS_PER_PAGE = 16;

function seedFiltersFromCurrentUser() {
    const user = getCurrentUser();
    if (!user) return null;

    const seeded = createEmptyCurriculumFilters();
    let touched = false;
    const mapping = [
        ['countryId', 'country_id'],
        ['regionId', 'region_id'],
        ['schoolTypeId', 'school_type_id'],
        ['gradeId', 'grade_id']
    ];

    for (const [filterKey, userKey] of mapping) {
        const value = sanitizeCurriculumFilterValue(user[userKey]);
        if (value) {
            seeded[filterKey] = value;
            touched = true;
        }
    }

    return touched ? seeded : null;
}

// ========== Micro-App Tool Call Bridge ==========
// Tracks the currently open app and its iframe for AI <-> micro-app communication

let currentOpenApp = null;      // The app object from the DB
let currentAppIframe = null;    // Reference to the iframe DOM element
let pendingToolCalls = {};      // call_id -> { resolve, reject, timeout }
let currentKeywordsModal = null;
let currentEducationContext = null;
let currentEducationContextPromise = null;
let currentEducationContextStatus = 'unknown';
const CURRICULUM_FILTER_FIELDS = [
    {
        key: 'countryId',
        lookupKey: 'countries',
        projectParam: 'curriculumCountryId',
        labels: { de: 'Land', en: 'Country' }
    },
    {
        key: 'regionId',
        lookupKey: 'regions',
        projectParam: 'curriculumRegionId',
        labels: { de: 'Region', en: 'Region' }
    },
    {
        key: 'schoolTypeId',
        lookupKey: 'schoolTypes',
        projectParam: 'curriculumSchoolTypeId',
        labels: { de: 'Schultyp', en: 'School Type' }
    },
    {
        key: 'subjectId',
        lookupKey: 'subjects',
        projectParam: 'curriculumSubjectId',
        labels: { de: 'Fach', en: 'Subject' }
    },
    {
        key: 'gradeId',
        lookupKey: 'grades',
        projectParam: 'curriculumGradeId',
        labels: { de: 'Klasse', en: 'Grade' }
    }
];

function createEmptyCurriculumFilters() {
    return {
        countryId: '',
        regionId: '',
        schoolTypeId: '',
        subjectId: '',
        gradeId: ''
    };
}

function createEmptyCurriculumLookups() {
    return {
        countries: [],
        regions: [],
        schoolTypes: [],
        subjects: [],
        grades: []
    };
}

function escapeHtml(value) {
    return String(value ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

function getFilterUiText(language = getCurrentLanguage()) {
    const isGerman = language === 'de' || language.startsWith('de-');
    return {
        title: isGerman ? 'Curriculum-Filter' : 'Curriculum Filters',
        hint: isGerman
            ? 'Filtere die Micro-Apps nach Land, Region, Schultyp, Fach und Klasse.'
            : 'Filter micro apps by country, region, school type, subject, and grade.',
        all: isGerman ? 'Alle' : 'All',
        reset: isGerman ? 'Zurücksetzen' : 'Reset',
        loading: isGerman ? 'Apps werden geladen...' : 'Loading apps...',
        noApps: isGerman ? 'Keine Anwendungen gefunden.' : 'No applications found.',
        noAppsFiltered: isGerman
            ? 'Keine Micro-Apps für die aktuellen Curriculum-Filter gefunden.'
            : 'No micro apps match the current curriculum filters.',
        failedLoad: isGerman ? 'Anwendungen konnten nicht geladen werden.' : 'Failed to load applications.'
    };
}

function hasActiveCurriculumFilters(filters = currentAppFilters) {
    return Object.values(filters).some((value) => String(value || '').trim().length > 0);
}

function sanitizeCurriculumFilterValue(value) {
    const normalized = String(value ?? '').trim();
    return /^\d+$/.test(normalized) ? normalized : '';
}

function getOptionSortKey(option) {
    return String(option?.name || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLocaleLowerCase('de');
}

function sortLookupOptions(options) {
    return [...(Array.isArray(options) ? options : [])].sort((a, b) => (
        getOptionSortKey(a).localeCompare(getOptionSortKey(b), 'de', {
            numeric: true,
            sensitivity: 'base'
        })
    ));
}

function appendCurriculumTenantScope(url) {
    const tenant = getCurrentTenant();
    const scope = tenant?.curriculumScope || {};

    url.searchParams.set('tenant', scope.tenant || tenant?.id || 'eu');

    Object.entries(scope).forEach(([key, value]) => {
        const normalized = String(value ?? '').trim();
        if (normalized) {
            url.searchParams.set(key, normalized);
        }
    });

    return url;
}

function buildProjectRequestUrl(language, filters = currentAppFilters, page = 1, limit = APPS_PER_PAGE) {
    const url = buildApiUrl(CONFIG.APP_API_BASE, '/api/projects');
    url.searchParams.set('lang', language);
    url.searchParams.set('page', String(page));
    url.searchParams.set('limit', String(limit));
    appendCurriculumTenantScope(url);

    CURRICULUM_FILTER_FIELDS.forEach((field) => {
        const value = sanitizeCurriculumFilterValue(filters[field.key]);
        if (value) {
            url.searchParams.set(field.projectParam, value);
        }
    });

    return url;
}

function buildCurriculumLookupUrl(language, filters = currentAppFilters) {
    const url = buildApiUrl(CONFIG.API_BASE, '/api/curriculum/lookups');
    url.searchParams.set('lang', language);
    appendCurriculumTenantScope(url);

    CURRICULUM_FILTER_FIELDS.forEach((field) => {
        const value = sanitizeCurriculumFilterValue(filters[field.key]);
        if (value) {
            url.searchParams.set(field.key, value);
        }
    });

    return url;
}

function getLookupFiltersForField(fieldKey, filters = currentAppFilters) {
    const scopedFilters = createEmptyCurriculumFilters();
    const fieldIndex = CURRICULUM_FILTER_FIELDS.findIndex((field) => field.key === fieldKey);

    if (fieldIndex === -1) {
        return scopedFilters;
    }

    for (let index = 0; index < fieldIndex; index += 1) {
        const parentField = CURRICULUM_FILTER_FIELDS[index];
        scopedFilters[parentField.key] = sanitizeCurriculumFilterValue(filters[parentField.key]);
    }

    return scopedFilters;
}

function normalizeCurriculumLookups(payload) {
    const fallback = createEmptyCurriculumLookups();
    if (!payload || typeof payload !== 'object') {
        return fallback;
    }

    return CURRICULUM_FILTER_FIELDS.reduce((lookups, field) => {
        lookups[field.lookupKey] = Array.isArray(payload[field.lookupKey])
            ? payload[field.lookupKey]
            : fallback[field.lookupKey];
        return lookups;
    }, {});
}

function getDependentCurriculumFilters(fieldKey, value) {
    const nextFilters = { ...currentAppFilters };
    const fieldIndex = CURRICULUM_FILTER_FIELDS.findIndex((field) => field.key === fieldKey);

    if (fieldIndex === -1) {
        return nextFilters;
    }

    nextFilters[fieldKey] = sanitizeCurriculumFilterValue(value);

    for (let index = fieldIndex + 1; index < CURRICULUM_FILTER_FIELDS.length; index += 1) {
        nextFilters[CURRICULUM_FILTER_FIELDS[index].key] = '';
    }

    return nextFilters;
}

function getAppsShell(container) {
    let filterPanel = container.querySelector('#apps-curriculum-filters');
    let contentPanel = container.querySelector('#app-grid-content');

    if (!filterPanel || !contentPanel) {
        container.innerHTML = `
            <section id="apps-curriculum-filters" class="apps-curriculum-filters"></section>
            <section id="app-grid-content"></section>
        `;
        filterPanel = container.querySelector('#apps-curriculum-filters');
        contentPanel = container.querySelector('#app-grid-content');
    }

    return { filterPanel, contentPanel };
}

function renderAppsLoading(container, message = getFilterUiText().loading) {
    container.innerHTML = `
        <div class="loading-apps">
            <div class="loading-spinner"></div>
            <p>${escapeHtml(message)}</p>
        </div>
    `;
}

function renderAppsError(container, message) {
    container.innerHTML = `
        <div style="text-align: center; padding: 2rem; color: var(--text-secondary);">
            <p>${escapeHtml(message)}</p>
            <button id="retry-apps-btn" style="margin-top: 1rem; padding: 0.5rem 1rem; background: var(--primary-color); color: white; border: none; border-radius: 0.5rem; cursor: pointer;">Retry</button>
        </div>
    `;

    const retryBtn = container.querySelector('#retry-apps-btn');
    if (retryBtn) {
        retryBtn.onclick = () => initApps();
    }
}

function renderCurriculumFilters(container) {
    if (!container) return;

    if (isUserSignedIn()) {
        container.innerHTML = '';
        container.style.display = 'none';
        renderSubjectOnlyFilter();
        return;
    }

    hideHeaderSubjectFilter();
    container.style.display = '';

    const language = getCurrentLanguage();
    const text = getFilterUiText(language);

    container.innerHTML = `
        <div class="apps-curriculum-filter-grid">
            ${CURRICULUM_FILTER_FIELDS.map((field) => {
                const options = sortLookupOptions(currentCurriculumLookups[field.lookupKey]);
                const selectedValue = String(currentAppFilters[field.key] || '');
                const label = field.labels[language === 'de' || language.startsWith('de-') ? 'de' : 'en'] || field.labels.en;

                return `
                    <label class="apps-curriculum-filter-field">
                        <span>${escapeHtml(label)}</span>
                        <select data-curriculum-filter="${field.key}">
                            <option value="">${escapeHtml(text.all)}</option>
                            ${options.map((option) => `
                                <option value="${escapeHtml(option.id)}"${String(option.id) === selectedValue ? ' selected' : ''}>
                                    ${escapeHtml(option.name)}
                                </option>
                            `).join('')}
                        </select>
                    </label>
                `;
            }).join('')}
            <button type="button" class="apps-curriculum-filter-reset" id="appsCurriculumResetBtn"${hasActiveCurriculumFilters() ? '' : ' disabled'}>
                ${escapeHtml(text.reset)}
            </button>
        </div>
    `;

    CURRICULUM_FILTER_FIELDS.forEach((field) => {
        const select = container.querySelector(`[data-curriculum-filter="${field.key}"]`);
        if (!select) return;

        select.addEventListener('change', async (event) => {
            currentAppFilters = getDependentCurriculumFilters(field.key, event.target.value);
            await initApps();
        });
    });

    const resetButton = container.querySelector('#appsCurriculumResetBtn');
    if (resetButton) {
        resetButton.addEventListener('click', async () => {
            currentAppFilters = createEmptyCurriculumFilters();
            autoEducationFiltersApplied = true;
            await initApps();
        });
    }
}

function renderSubjectOnlyFilter() {
    const headerSlot = document.getElementById('headerSubjectFilter');
    if (!headerSlot) return;

    const isAppOpenInUI = Boolean(currentOpenApp);
    if (isAppOpenInUI) {
        headerSlot.style.display = 'none';
        return;
    }

    const language = getCurrentLanguage();
    const isGerman = language === 'de' || language.startsWith('de-');
    const subjectField = CURRICULUM_FILTER_FIELDS.find((field) => field.key === 'subjectId');
    const subjectLabel = subjectField
        ? (subjectField.labels[isGerman ? 'de' : 'en'] || subjectField.labels.en)
        : (isGerman ? 'Fach' : 'Subject');
    const allLabel = isGerman ? 'Alle Fächer' : 'All subjects';
    const subjects = sortLookupOptions(currentCurriculumLookups.subjects);
    const selectedValue = String(currentAppFilters.subjectId || '');

    headerSlot.innerHTML = `
        <label class="header-subject-filter-field">
            <span>${escapeHtml(subjectLabel)}</span>
            <select id="appsSubjectFilter">
                <option value="">${escapeHtml(allLabel)}</option>
                ${subjects.map((option) => `
                    <option value="${escapeHtml(option.id)}"${String(option.id) === selectedValue ? ' selected' : ''}>
                        ${escapeHtml(option.name)}
                    </option>
                `).join('')}
            </select>
        </label>
    `;
    headerSlot.style.display = 'flex';

    const select = headerSlot.querySelector('#appsSubjectFilter');
    if (!select) return;

    select.addEventListener('change', async (event) => {
        currentAppFilters = {
            ...currentAppFilters,
            subjectId: sanitizeCurriculumFilterValue(event.target.value)
        };
        await initApps();
    });
}

function hideHeaderSubjectFilter() {
    const headerSlot = document.getElementById('headerSubjectFilter');
    if (!headerSlot) return;
    headerSlot.style.display = 'none';
    headerSlot.innerHTML = '';
}

async function requestSubjectsLookup(language, scopeFilters) {
    const url = buildCurriculumLookupUrl(language, scopeFilters);
    const response = await fetch(url.toString(), { cache: 'no-store' });
    const payload = await response.json().catch(() => null);

    if (!response.ok) {
        throw new Error(payload?.error || 'Failed to load subjects.');
    }

    const normalized = normalizeCurriculumLookups(payload);
    return normalized.subjects || [];
}

async function requestCurriculumLookups(language, filters = currentAppFilters) {
    const lookupEntries = await Promise.all(CURRICULUM_FILTER_FIELDS.map(async (field) => {
        const response = await fetch(
            buildCurriculumLookupUrl(language, getLookupFiltersForField(field.key, filters)).toString(),
            { cache: 'no-store' }
        );
        const payload = await response.json().catch(() => null);

        if (!response.ok) {
            throw new Error(payload?.error || 'Failed to load curriculum lookups.');
        }

        const normalizedPayload = normalizeCurriculumLookups(payload);
        return [field.lookupKey, normalizedPayload[field.lookupKey] || []];
    }));

    return lookupEntries.reduce((lookups, [lookupKey, options]) => {
        lookups[lookupKey] = options;
        return lookups;
    }, createEmptyCurriculumLookups());
}

function getAppId(app) {
    return app?.id || app?._id || null;
}

function getCurrentLanguage() {
    if (window.i18n && typeof window.i18n.getCurrentLanguage === 'function') {
        return window.i18n.getCurrentLanguage();
    }

    return localStorage.getItem('appLanguage') || 'de';
}

function tr(key, variables, fallback) {
    if (window.i18n && typeof window.i18n.t === 'function') {
        const value = window.i18n.t(key, variables || {});
        if (value && value !== key) return value;
    }
    return fallback;
}

function getNestedValue(source, path) {
    return path.reduce((value, key) => (value && value[key] !== undefined ? value[key] : undefined), source);
}

function getNumericField(source, candidatePaths) {
    for (const path of candidatePaths) {
        const rawValue = getNestedValue(source, path);
        if (rawValue === undefined || rawValue === null || rawValue === '') continue;

        const numericValue = Number(rawValue);
        if (Number.isFinite(numericValue) && numericValue > 0) {
            return numericValue;
        }
    }

    return null;
}

function getCurriculumScope(app) {
    return {
        country_id: getNumericField(app, [
            ['country_id'],
            ['countryId'],
            ['country', 'id'],
            ['curriculum', 'country_id'],
            ['curriculum', 'countryId']
        ]),
        school_type_id: getNumericField(app, [
            ['school_type_id'],
            ['schoolTypeId'],
            ['school_type', 'id'],
            ['schoolType', 'id'],
            ['curriculum', 'school_type_id'],
            ['curriculum', 'schoolTypeId']
        ]),
        grade_id: getNumericField(app, [
            ['grade_id'],
            ['gradeId'],
            ['grade', 'id'],
            ['curriculum', 'grade_id'],
            ['curriculum', 'gradeId']
        ]),
        region_id: getNumericField(app, [
            ['region_id'],
            ['regionId'],
            ['region', 'id'],
            ['curriculum', 'region_id'],
            ['curriculum', 'regionId']
        ])
    };
}

function buildApiUrl(base, path) {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const baseOrigin = base && base.trim().length > 0 ? base : window.location.origin;
    return new URL(normalizedPath, baseOrigin);
}

async function logApiDebug(label, requestUrl, response) {
    const responseClone = response.clone();
    const bodySnippet = await responseClone.text()
        .then(text => text.slice(0, 300))
        .catch(() => '[body unreadable]');

    console.log(`[apps debug] ${label}`, {
        requestUrl,
        responseUrl: response.url,
        status: response.status,
        statusText: response.statusText,
        contentType: response.headers.get('content-type'),
        server: response.headers.get('server'),
        via: response.headers.get('via'),
        xVercelId: response.headers.get('x-vercel-id'),
        bodySnippet
    });
}

function mergeCurriculumScope(appScope, educationContext = null) {
    return {
        country_id: educationContext?.country_id || appScope.country_id || null,
        school_type_id: educationContext?.school_type_id || appScope.school_type_id || null,
        grade_id: educationContext?.grade_id || appScope.grade_id || null,
        region_id: educationContext?.region_id || appScope.region_id || null
    };
}

function getCurriculumScopeLabels(scope, educationContext = null) {
    return {
        country_name: educationContext?.country_name || null,
        region_name: educationContext?.region_name || null,
        school_type_name: educationContext?.school_type_name || null,
        school_type_level: educationContext?.school_type_level || null,
        grade_name: educationContext?.grade_name || null,
        scope
    };
}

function normalizeEducationContext(payload) {
    if (!payload) {
        return null;
    }

    const context = {
        student_id: payload.student_id || payload.id || null,
        country_id: payload.country_id || null,
        country_name: payload.country_name || null,
        region_id: payload.region_id || null,
        region_name: payload.region_name || null,
        school_type_id: payload.school_type_id || null,
        school_type_name: payload.school_type_name || null,
        school_type_level: payload.school_type_level || null,
        grade_id: payload.grade_id || null,
        grade_name: payload.grade_name || null
    };

    const hasEducationFields = Boolean(
        context.country_id ||
        context.region_id ||
        context.school_type_id ||
        context.grade_id
    );

    return hasEducationFields ? context : null;
}

async function fetchWith404Fallback(path, options = {}) {
    const candidateBases = [CONFIG.AUTH_API_BASE, CONFIG.API_BASE];
    const attemptedUrls = new Set();
    let lastNotFoundResponse = null;

    for (const base of candidateBases) {
        const url = `${base}${path}`;
        if (attemptedUrls.has(url)) continue;
        attemptedUrls.add(url);

        const response = await fetch(url, options);
        await logApiDebug('fetchWith404Fallback', url, response);
        if (response.status === 404) {
            lastNotFoundResponse = response;
            continue;
        }

        return response;
    }

    return lastNotFoundResponse;
}

function educationContextHasNames(context) {
    if (!context) return false;
    const hasNameForId = (idKey, nameKey) => !context[idKey] || (typeof context[nameKey] === 'string' && context[nameKey].trim().length > 0);
    return hasNameForId('country_id', 'country_name')
        && hasNameForId('region_id', 'region_name')
        && hasNameForId('school_type_id', 'school_type_name')
        && hasNameForId('grade_id', 'grade_name');
}

async function fetchStudentEducationContext() {
    if (currentEducationContext && educationContextHasNames(currentEducationContext)) {
        return currentEducationContext;
    }

    const storedUser = getCurrentUser();
    const storedContext = normalizeEducationContext(storedUser);
    if (storedContext && educationContextHasNames(storedContext)) {
        currentEducationContext = storedContext;
        currentEducationContextStatus = 'loaded_from_login_profile';
        return storedContext;
    }

    const token = getToken();
    if (!token) {
        if (storedContext) {
            currentEducationContext = storedContext;
            currentEducationContextStatus = 'loaded_from_login_profile';
            return storedContext;
        }
        currentEducationContextStatus = 'no_token';
        return null;
    }

    if (currentEducationContextPromise) {
        return currentEducationContextPromise;
    }

    currentEducationContextPromise = (async () => {
        const headers = {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };

        const profileResponse = await fetchWith404Fallback('/api/child/me', {
            headers
        });

        if (profileResponse?.ok) {
            const profilePayload = await profileResponse.json();
            const normalizedProfile = normalizeEducationContext(profilePayload);
            if (normalizedProfile) {
                currentEducationContext = normalizedProfile;
                currentEducationContextStatus = 'loaded_from_child_me';
                return normalizedProfile;
            }
            currentEducationContextStatus = 'child_me_without_education_fields';
        } else if (profileResponse) {
            const authResult = await handleAuthError(profileResponse);
            if (authResult.handled) {
                throw new Error(authResult.error);
            }

            if (profileResponse.status === 404) {
                currentEducationContextStatus = 'child_profile_not_found';
                console.warn('fetchStudentEducationContext: child/me returned 404; the token does not resolve to a child profile on the backend.');
            }
        } else {
            currentEducationContextStatus = 'child_me_unreachable';
        }

        if (storedContext) {
            currentEducationContext = storedContext;
            return storedContext;
        }
        return null;
    })();

    try {
        return await currentEducationContextPromise;
    } finally {
        currentEducationContextPromise = null;
    }
}

function buildTenantHeader() {
    try {
        const tenant = getCurrentTenant();
        return tenant?.id || null;
    } catch {
        return null;
    }
}

function buildAuthHeaders(extra = {}) {
    const headers = { ...extra };
    const token = getToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    const tenantId = buildTenantHeader();
    if (tenantId) {
        headers['X-Tenant'] = tenantId;
    }
    return headers;
}

function normalizeTopicPayload(raw) {
    if (!raw || typeof raw !== 'object') return null;

    const source = raw.topic && typeof raw.topic === 'object' ? raw.topic : raw;

    const toList = (value) => {
        if (Array.isArray(value)) {
            return value.map((item) => String(item ?? '').trim()).filter(Boolean);
        }
        if (typeof value === 'string') {
            return value
                .split(/\r?\n|·|\|/)
                .map((item) => item.replace(/^[-*•\d.\s]+/, '').trim())
                .filter(Boolean);
        }
        return [];
    };

    const stringField = (...keys) => {
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.trim().length > 0) return value.trim();
        }
        return '';
    };

    const numericField = (...keys) => {
        for (const key of keys) {
            const value = source[key];
            if (value === null || value === undefined || value === '') continue;
            const numeric = Number(value);
            if (Number.isFinite(numeric) && numeric > 0) return numeric;
        }
        return null;
    };

    const topic = {
        topic_name: stringField('topic_name', 'topicName'),
        unit_name: stringField('unit_name', 'unitName'),
        subtopic_name: stringField('subtopic_name', 'subtopicName'),
        grade_name: stringField('grade_name', 'gradeName'),
        school_type_name: stringField('school_type_name', 'schoolTypeName'),
        subject_name: stringField('subject_name', 'subjectName'),
        country_name: stringField('country_name', 'countryName'),
        description: stringField('description'),
        learning_goals: toList(source.learning_goals ?? source.learningGoals),
        prerequisites: toList(source.prerequisites),
        duration_minutes: numericField('duration_minutes', 'durationMinutes'),
        difficulty: stringField('difficulty')
    };

    const hasContent = Boolean(
        topic.topic_name || topic.subtopic_name || topic.unit_name ||
        topic.description || topic.learning_goals.length > 0
    );
    return hasContent ? topic : null;
}

function buildInlineTopicFallback(app) {
    const inline = app?.curriculum && typeof app.curriculum === 'object' ? app.curriculum : null;
    if (!inline) return null;
    const fallback = {
        country_name: inline.countryName || inline.country_name || '',
        school_type_name: inline.schoolTypeName || inline.school_type_name || '',
        subject_name: inline.subjectName || inline.subject_name || '',
        grade_name: inline.gradeName || inline.grade_name || ''
    };
    const hasAny = Object.values(fallback).some((value) => value && String(value).trim().length > 0);
    return hasAny ? fallback : null;
}

async function fetchAppCurriculumTopic(app) {
    const appId = getAppId(app);
    if (!appId) {
        throw new Error('App ID fehlt.');
    }

    const url = buildApiUrl(CONFIG.API_BASE, `/api/applications/${encodeURIComponent(appId)}/curriculum-topic`);
    let payload = null;
    try {
        const response = await fetch(url.toString(), { headers: buildAuthHeaders() });
        if (response.status === 404) {
            return buildInlineTopicFallback(app);
        }
        if (!response.ok) {
            throw new Error(`Topic-Abruf fehlgeschlagen (${response.status}).`);
        }
        payload = await response.json().catch(() => null);
    } catch (error) {
        console.warn('curriculum-topic fetch failed:', error);
        return buildInlineTopicFallback(app);
    }

    const normalized = normalizeTopicPayload(payload);
    if (normalized) {
        const inline = buildInlineTopicFallback(app) || {};
        return {
            ...inline,
            ...Object.fromEntries(Object.entries(normalized).filter(([, value]) => value && (Array.isArray(value) ? value.length > 0 : true)))
        };
    }

    return buildInlineTopicFallback(app);
}

function buildExplanationStudentContext(educationContext) {
    if (!educationContext) return {};
    return {
        country_name: educationContext.country_name || '',
        region_name: educationContext.region_name || '',
        school_type_name: educationContext.school_type_name || '',
        school_type_level: educationContext.school_type_level || '',
        grade_name: educationContext.grade_name || ''
    };
}

async function requestKidFriendlyExplanation(app, topic, educationContext, language) {
    const url = buildApiUrl('', '/api/curriculum-topic-explanation');
    const response = await fetch(url.toString(), {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
            appName: app?.name || '',
            language: language || getCurrentLanguage(),
            topic,
            studentContext: buildExplanationStudentContext(educationContext)
        })
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload?.error || tr('curriculum.explanation.requestFailed', null, 'Kindgerechte Erklärung konnte nicht erzeugt werden.'));
    }
    const explanation = typeof payload.explanation === 'string' && payload.explanation.trim().length > 0
        ? payload.explanation.trim()
        : null;
    const curriculumSourceText = typeof payload.curriculumSourceText === 'string' && payload.curriculumSourceText.trim().length > 0
        ? payload.curriculumSourceText.trim()
        : null;
    return { explanation, curriculumSourceText };
}

async function requestCurriculumChatReply({ app, topic, educationContext, language, conversationId, message, initialExplanation, curriculumSourceText }) {
    const url = buildApiUrl('', '/api/curriculum-topic-chat');
    const body = {
        message,
        language: language || getCurrentLanguage()
    };
    if (conversationId) {
        body.conversationId = conversationId;
    } else {
        const appId = getAppId(app);
        if (appId !== null && appId !== undefined && appId !== '') {
            body.appId = appId;
        }
        if (app?.name) {
            body.appName = app.name;
        }
        body.topic = topic || {};
        body.studentContext = buildExplanationStudentContext(educationContext);
        if (typeof initialExplanation === 'string' && initialExplanation.trim().length > 0) {
            body.initialExplanation = initialExplanation.trim();
        }
        if (typeof curriculumSourceText === 'string' && curriculumSourceText.trim().length > 0) {
            body.curriculumSourceText = curriculumSourceText.trim();
        }
    }

    const response = await fetch(url.toString(), {
        method: 'POST',
        headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify(body)
    });

    const payload = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(payload?.error || payload?.detail || tr('curriculum.chat.requestFailed', null, 'Antwort konnte nicht erzeugt werden.'));
    }
    return {
        conversationId: payload?.conversationId || conversationId || null,
        reply: typeof payload?.reply === 'string' ? payload.reply.trim() : ''
    };
}

async function fetchLatestCurriculumChatId(app) {
    const appId = getAppId(app);
    if (!appId) return null;

    const url = buildApiUrl('', '/api/curriculum-topic-chat/conversations');
    url.searchParams.set('app_id', String(appId));
    url.searchParams.set('limit', '1');
    url.searchParams.set('offset', '0');

    try {
        const response = await fetch(url.toString(), { headers: buildAuthHeaders() });
        if (!response.ok) return null;
        const payload = await response.json().catch(() => null);
        const items = Array.isArray(payload?.items) ? payload.items : [];
        return items.length > 0 && items[0]?.id ? items[0].id : null;
    } catch (error) {
        console.warn('curriculum-chat list fetch failed:', error);
        return null;
    }
}

async function fetchCurriculumChatDetail(conversationId) {
    if (!conversationId) return null;

    const url = buildApiUrl('', `/api/curriculum-topic-chat/conversations/${encodeURIComponent(conversationId)}`);
    try {
        const response = await fetch(url.toString(), { headers: buildAuthHeaders() });
        if (!response.ok) return null;
        const payload = await response.json().catch(() => null);
        if (!payload || !Array.isArray(payload.messages)) return null;
        const messages = payload.messages
            .filter((m) => m && (m.role === 'user' || m.role === 'assistant') && typeof m.content === 'string')
            .map((m) => ({ role: m.role, content: m.content }));
        const snapshotSource = typeof payload?.topicSnapshot?.curriculumSourceText === 'string'
            ? payload.topicSnapshot.curriculumSourceText
            : null;
        return { id: payload.id, messages, curriculumSourceText: snapshotSource };
    } catch (error) {
        console.warn('curriculum-chat detail fetch failed:', error);
        return null;
    }
}

async function prepareCurriculumPayload(app) {
    const appScope = getCurriculumScope(app);
    const educationContext = await fetchStudentEducationContext().catch(() => null);
    const mergedScope = mergeCurriculumScope(appScope, educationContext);
    const scopeLabels = getCurriculumScopeLabels(mergedScope, educationContext);

    const [topic, latestChatId] = await Promise.all([
        fetchAppCurriculumTopic(app).catch((error) => {
            console.error('Failed to load curriculum topic:', error);
            return null;
        }),
        fetchLatestCurriculumChatId(app)
    ]);

    const chatDetail = latestChatId ? await fetchCurriculumChatDetail(latestChatId) : null;

    const topicHasDetail = Boolean(
        topic && (
            topic.topic_name ||
            topic.subtopic_name ||
            topic.description ||
            (Array.isArray(topic.learning_goals) && topic.learning_goals.length > 0)
        )
    );

    const allMessages = chatDetail?.messages || [];
    const firstAssistantIdx = allMessages.findIndex((m) => m.role === 'assistant');
    const seedExplanation = firstAssistantIdx >= 0 ? allMessages[firstAssistantIdx].content : null;
    const hasSeedExplanation = typeof seedExplanation === 'string' && seedExplanation.trim().length > 0;
    const visibleHistory = firstAssistantIdx >= 0
        ? [...allMessages.slice(0, firstAssistantIdx), ...allMessages.slice(firstAssistantIdx + 1)]
        : allMessages;

    let explanationState;
    if (hasSeedExplanation) {
        explanationState = 'ready';
    } else if (topicHasDetail) {
        explanationState = 'loading';
    } else {
        explanationState = 'unavailable';
    }

    return {
        topic,
        topicState: topic ? 'ready' : 'unavailable',
        topicHasDetail,
        explanation: hasSeedExplanation ? seedExplanation : null,
        curriculumSourceText: chatDetail?.curriculumSourceText || null,
        explanationState,
        scope: mergedScope,
        scopeLabels,
        educationContext,
        educationContextStatus: currentEducationContextStatus,
        chatConversationId: chatDetail?.id || null,
        chatHistory: visibleHistory
    };
}

function closeKeywordsModal() {
    if (!currentKeywordsModal) return;

    const { overlay, onKeyDown, triggerButton } = currentKeywordsModal;

    if (onKeyDown) {
        document.removeEventListener('keydown', onKeyDown);
    }

    if (triggerButton) {
        triggerButton.classList.remove('active');
        triggerButton.setAttribute('aria-expanded', 'false');
    }

    overlay.remove();
    currentKeywordsModal = null;
}

function collectStudentContextEntries(curriculumPayload) {
    const scopeLabels = curriculumPayload.scopeLabels || {};
    const scope = curriculumPayload.scope || {};
    const topic = curriculumPayload.topic || null;

    const entries = [
        {
            label: tr('curriculum.studentProfile.country', null, 'Land'),
            value: scopeLabels.country_name || topic?.country_name || (scope.country_id ? `ID ${scope.country_id}` : null)
        },
        {
            label: tr('curriculum.studentProfile.region', null, 'Bundesland'),
            value: scopeLabels.region_name || (scope.region_id ? `ID ${scope.region_id}` : null)
        },
        {
            label: tr('curriculum.studentProfile.schoolType', null, 'Schulform'),
            value: scopeLabels.school_type_name || topic?.school_type_name || (scope.school_type_id ? `ID ${scope.school_type_id}` : null)
        },
        {
            label: tr('curriculum.studentProfile.grade', null, 'Klasse'),
            value: scopeLabels.grade_name || topic?.grade_name || (scope.grade_id ? `ID ${scope.grade_id}` : null)
        }
    ].filter(entry => Boolean(entry.value));

    return entries;
}

function buildStudentContextSection(curriculumPayload) {
    const section = document.createElement('section');
    section.className = 'app-keywords-section';

    const heading = document.createElement('div');
    heading.className = 'app-keywords-locale';
    heading.textContent = tr('curriculum.sections.studentProfile', null, 'Schülerprofil');
    section.appendChild(heading);

    const details = document.createElement('div');
    details.className = 'app-keywords-list';

    const entries = collectStudentContextEntries(curriculumPayload);

    if (entries.length === 0) {
        const empty = document.createElement('div');
        empty.className = 'app-keywords-empty';
        if (curriculumPayload.educationContextStatus === 'child_profile_not_found') {
            empty.textContent = tr('curriculum.studentProfile.childProfileNotFound', null, 'Zum aktuellen Login-Token wurde kein Schülerprofil im Backend gefunden.');
        } else if (curriculumPayload.educationContextStatus === 'loaded_from_login_profile') {
            empty.textContent = tr('curriculum.studentProfile.loginProfileIncomplete', null, 'Das Login-Profil enthält noch keinen vollständigen Schülerkontext.');
        } else if (curriculumPayload.educationContextStatus === 'education_context_not_found') {
            empty.textContent = tr('curriculum.studentProfile.educationContextNotFound', null, 'Es wurde ein Schülerprofil gefunden, aber keine Bildungszuordnung.');
        } else if (curriculumPayload.educationContextStatus === 'no_token') {
            empty.textContent = tr('curriculum.studentProfile.noToken', null, 'Kein eingeloggter Schüler gefunden.');
        } else {
            empty.textContent = tr('curriculum.studentProfile.noContext', null, 'Kein Schülerkontext verfügbar.');
        }
        section.appendChild(empty);
        return section;
    }

    entries.forEach(entry => {
        const chip = document.createElement('span');
        chip.className = 'app-keyword-chip';
        chip.textContent = `${entry.label}: ${entry.value}`;
        details.appendChild(chip);
    });

    section.appendChild(details);
    return section;
}

function appendInlineFormattedText(target, text) {
    const parts = String(text || '').split(/(\*\*[^*]+\*\*)/g).filter(Boolean);

    parts.forEach((part) => {
        if (part.startsWith('**') && part.endsWith('**') && part.length > 4) {
            const strong = document.createElement('strong');
            strong.textContent = part.slice(2, -2);
            target.appendChild(strong);
            return;
        }

        target.appendChild(document.createTextNode(part));
    });
}

function buildSummaryLoadingState(content, badgeText) {
    if (!badgeText) {
        badgeText = tr('curriculum.explanation.loadingPrepare', null, 'KI bereitet Erklärung vor');
    }
    const lines = [92, 78, 84, 66];

    const badge = document.createElement('div');
    badge.className = 'curriculum-summary-loading-badge';
    badge.textContent = badgeText;
    content.appendChild(badge);

    const skeleton = document.createElement('div');
    skeleton.className = 'curriculum-summary-skeleton';

    lines.forEach((width) => {
        const line = document.createElement('span');
        line.className = 'curriculum-summary-skeleton-line';
        line.style.width = `${width}%`;
        skeleton.appendChild(line);
    });

    content.appendChild(skeleton);
}

function renderTopicSectionContent(content, topic) {
    content.replaceChildren();

    if (!topic) {
        const empty = document.createElement('div');
        empty.className = 'app-keywords-empty';
        empty.textContent = tr('curriculum.topic.empty', null, 'Für diese Micro-App ist im Lehrplan-Backend kein Thema hinterlegt.');
        content.appendChild(empty);
        return;
    }

    const chips = [
        topic.country_name,
        topic.school_type_name,
        topic.subject_name,
        topic.grade_name
    ].filter(Boolean);

    if (chips.length > 0) {
        const chipsRow = document.createElement('div');
        chipsRow.className = 'curriculum-topic-chips';
        chips.forEach((value) => {
            const chip = document.createElement('span');
            chip.className = 'app-keyword-chip';
            chip.textContent = value;
            chipsRow.appendChild(chip);
        });
        content.appendChild(chipsRow);
    }

    if (topic.topic_name) {
        const title = document.createElement('h4');
        title.className = 'curriculum-topic-title';
        title.textContent = topic.topic_name;
        content.appendChild(title);
    }

    if (topic.subtopic_name) {
        const goal = document.createElement('p');
        goal.className = 'curriculum-topic-goal';
        goal.textContent = topic.subtopic_name;
        content.appendChild(goal);
    }

    const metaParts = [];
    if (topic.unit_name) {
        metaParts.push(`${tr('curriculum.topic.unitLabel', null, 'Einheit')}: ${topic.unit_name}`);
    }
    if (topic.duration_minutes) {
        const hours = Math.floor(topic.duration_minutes / 60);
        const minutes = topic.duration_minutes % 60;
        const hoursLabel = tr('curriculum.topic.hours', null, 'Std.');
        const minutesLabel = tr('curriculum.topic.minutes', null, 'Min.');
        const formatted = hours > 0 ? `${hours} ${hoursLabel} ${minutes} ${minutesLabel}` : `${minutes} ${minutesLabel}`;
        metaParts.push(`${tr('curriculum.topic.durationLabel', null, 'Dauer')}: ${formatted}`);
    }
    if (topic.difficulty) {
        metaParts.push(`${tr('curriculum.topic.difficultyLabel', null, 'Schwierigkeit')}: ${topic.difficulty}`);
    }
    if (metaParts.length > 0) {
        const meta = document.createElement('p');
        meta.className = 'app-keywords-meta app-keywords-meta-standalone';
        meta.textContent = metaParts.join(' · ');
        content.appendChild(meta);
    }

    if (topic.description) {
        const desc = document.createElement('p');
        desc.className = 'curriculum-topic-description';
        desc.textContent = topic.description;
        content.appendChild(desc);
    }

    const hasDetail = Boolean(topic.topic_name || topic.subtopic_name || topic.description || (Array.isArray(topic.learning_goals) && topic.learning_goals.length > 0));
    if (!hasDetail) {
        const note = document.createElement('div');
        note.className = 'app-keywords-empty';
        note.textContent = tr('curriculum.topic.missingDetail', null, 'Detaillierte Lehrplan-Daten zu dieser Micro-App stehen im Backend noch nicht zur Verfügung.');
        content.appendChild(note);
    }

    if (Array.isArray(topic.learning_goals) && topic.learning_goals.length > 0) {
        const heading = document.createElement('h5');
        heading.className = 'curriculum-summary-heading';
        heading.textContent = tr('curriculum.topic.learningGoals', null, 'Lernziele');
        content.appendChild(heading);

        const list = document.createElement('ul');
        list.className = 'curriculum-summary-list';
        topic.learning_goals.forEach((goal) => {
            const li = document.createElement('li');
            li.textContent = goal;
            list.appendChild(li);
        });
        content.appendChild(list);
    }

    if (Array.isArray(topic.prerequisites) && topic.prerequisites.length > 0) {
        const heading = document.createElement('h5');
        heading.className = 'curriculum-summary-heading';
        heading.textContent = tr('curriculum.topic.prerequisites', null, 'Voraussetzungen');
        content.appendChild(heading);

        const list = document.createElement('ul');
        list.className = 'curriculum-summary-list';
        topic.prerequisites.forEach((req) => {
            const li = document.createElement('li');
            li.textContent = req;
            list.appendChild(li);
        });
        content.appendChild(list);
    }
}

function buildTopicSection(curriculumPayload) {
    const section = document.createElement('section');
    section.className = 'app-keywords-section';

    const heading = document.createElement('div');
    heading.className = 'app-keywords-locale';
    heading.textContent = tr('curriculum.sections.topic', null, 'Lehrplan-Thema');
    section.appendChild(heading);

    const content = document.createElement('div');
    content.className = 'curriculum-topic';
    renderTopicSectionContent(content, curriculumPayload.topic);
    section.appendChild(content);
    section._topicContent = content;
    return section;
}

function renderExplanationContent(content, explanationText, isLoading) {
    content.replaceChildren();

    if (isLoading) {
        buildSummaryLoadingState(content, tr('curriculum.explanation.loadingExplain', null, 'KI erklärt das Thema kindgerecht'));
        return;
    }

    if (!explanationText) {
        content.textContent = tr('curriculum.explanation.empty', null, 'Keine kindgerechte Erklärung verfügbar.');
        return;
    }

    const lines = String(explanationText).replace(/\r/g, '').split('\n').map((line) => line.trimEnd());
    let currentList = null;
    const ensureListClosed = () => { currentList = null; };

    lines.forEach((rawLine) => {
        const line = rawLine.trim();
        if (!line) { ensureListClosed(); return; }

        const headingMatch = line.match(/^(\d+\.\s+)?\*\*(.+?)\*\*:?\s*$/);
        if (headingMatch) {
            ensureListClosed();
            const heading = document.createElement(headingMatch[1] ? 'h5' : 'h4');
            heading.className = 'curriculum-summary-heading';
            heading.textContent = headingMatch[2];
            content.appendChild(heading);
            return;
        }

        const bulletMatch = line.match(/^[-*]\s+(.+)$/);
        if (bulletMatch) {
            if (!currentList) {
                currentList = document.createElement('ul');
                currentList.className = 'curriculum-summary-list';
                content.appendChild(currentList);
            }
            const item = document.createElement('li');
            appendInlineFormattedText(item, bulletMatch[1]);
            currentList.appendChild(item);
            return;
        }

        ensureListClosed();
        const paragraph = document.createElement('p');
        paragraph.className = content.children.length === 0
            ? 'curriculum-summary-paragraph curriculum-summary-lead'
            : 'curriculum-summary-paragraph';
        appendInlineFormattedText(paragraph, line.replace(/^#+\s*/, ''));
        content.appendChild(paragraph);
    });
}

function buildExplanationSection(curriculumPayload) {
    const section = document.createElement('section');
    section.className = 'app-keywords-section';

    const heading = document.createElement('div');
    heading.className = 'app-keywords-locale';
    heading.textContent = tr('curriculum.sections.explanation', null, 'Kindgerechte Erklärung');
    section.appendChild(heading);

    const content = document.createElement('div');
    content.className = 'curriculum-bridge-summary';
    content.classList.toggle('curriculum-bridge-summary-loading', curriculumPayload.explanationState === 'loading');
    renderExplanationContent(
        content,
        curriculumPayload.explanation,
        curriculumPayload.explanationState === 'loading'
    );

    section.appendChild(content);
    section._explanationContent = content;
    return section;
}

function appendChatMessage(historyEl, role, text) {
    const bubble = document.createElement('div');
    bubble.className = `curriculum-chat-message curriculum-chat-message-${role}`;

    const inner = document.createElement('div');
    inner.className = 'curriculum-chat-bubble';

    if (role === 'assistant') {
        renderExplanationContent(inner, text, false);
    } else {
        const paragraph = document.createElement('p');
        paragraph.className = 'curriculum-chat-text';
        paragraph.textContent = text;
        inner.appendChild(paragraph);
    }

    bubble.appendChild(inner);
    historyEl.appendChild(bubble);
    historyEl.scrollTop = historyEl.scrollHeight;
    return bubble;
}

function appendChatTypingIndicator(historyEl) {
    const bubble = document.createElement('div');
    bubble.className = 'curriculum-chat-message curriculum-chat-message-assistant curriculum-chat-message-typing';

    const inner = document.createElement('div');
    inner.className = 'curriculum-chat-bubble';

    const dots = document.createElement('div');
    dots.className = 'curriculum-chat-typing';
    dots.innerHTML = '<span></span><span></span><span></span>';
    inner.appendChild(dots);

    bubble.appendChild(inner);
    historyEl.appendChild(bubble);
    historyEl.scrollTop = historyEl.scrollHeight;
    return bubble;
}

function buildChatSection(curriculumPayload, app) {
    const section = document.createElement('section');
    section.className = 'app-keywords-section';

    const heading = document.createElement('div');
    heading.className = 'app-keywords-locale';
    heading.textContent = tr('curriculum.sections.chat', null, 'Fragen an die KI');
    section.appendChild(heading);

    const parentNotice = document.createElement('p');
    parentNotice.className = 'parent-visibility-notice';
    parentNotice.setAttribute('role', 'note');
    parentNotice.textContent = tr('curriculum.chat.parentNotice', null, 'Hinweis: Deine Eltern können diese Unterhaltung jederzeit lesen.');
    section.appendChild(parentNotice);

    const wrapper = document.createElement('div');
    wrapper.className = 'curriculum-chat';

    const history = document.createElement('div');
    history.className = 'curriculum-chat-history';
    history.setAttribute('aria-live', 'polite');

    const existingMessages = Array.isArray(curriculumPayload?.chatHistory) ? curriculumPayload.chatHistory : [];
    if (existingMessages.length > 0) {
        for (const msg of existingMessages) {
            appendChatMessage(history, msg.role, msg.content);
        }
    } else {
        const hint = document.createElement('p');
        hint.className = 'curriculum-chat-hint';
        hint.textContent = tr('curriculum.chat.hint', null, 'Stelle eine Frage zum Lehrplan-Thema. Die KI hilft dir zu verstehen, warum dieses Thema wichtig ist.');
        history.appendChild(hint);
    }

    wrapper.appendChild(history);

    const form = document.createElement('form');
    form.className = 'curriculum-chat-form';
    form.setAttribute('autocomplete', 'off');

    const textarea = document.createElement('textarea');
    textarea.className = 'curriculum-chat-input';
    textarea.rows = 1;
    textarea.maxLength = 2000;
    textarea.placeholder = tr('curriculum.chat.placeholder', null, 'Frage eingeben …');
    textarea.setAttribute('aria-label', tr('curriculum.chat.inputAria', null, 'Frage an die KI'));

    const sendBtn = document.createElement('button');
    sendBtn.type = 'submit';
    sendBtn.className = 'curriculum-chat-send';
    sendBtn.textContent = tr('curriculum.chat.send', null, 'Senden');

    form.appendChild(textarea);
    form.appendChild(sendBtn);
    wrapper.appendChild(form);
    section.appendChild(wrapper);

    const topicHasDetail = Boolean(curriculumPayload?.topicHasDetail);
    if (!topicHasDetail) {
        textarea.disabled = true;
        sendBtn.disabled = true;
        textarea.placeholder = tr('curriculum.chat.noTopic', null, 'Für dieses Thema steht kein Lehrplan-Inhalt für Fragen bereit.');
    }

    const autoresize = () => {
        textarea.style.height = 'auto';
        textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
    };
    textarea.addEventListener('input', autoresize);
    textarea.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            form.requestSubmit();
        }
    });

    let isSending = false;

    form.addEventListener('submit', async (event) => {
        event.preventDefault();
        if (isSending) return;
        const text = textarea.value.trim();
        if (!text) return;

        isSending = true;
        textarea.disabled = true;
        sendBtn.disabled = true;
        sendBtn.textContent = tr('curriculum.chat.sending', null, 'Sendet …');

        appendChatMessage(history, 'user', text);
        textarea.value = '';
        autoresize();

        const typing = appendChatTypingIndicator(history);

        try {
            const result = await requestCurriculumChatReply({
                app,
                topic: curriculumPayload.topic,
                educationContext: curriculumPayload.educationContext,
                language: getCurrentLanguage(),
                conversationId: curriculumPayload.chatConversationId || null,
                message: text,
                initialExplanation: curriculumPayload.explanation || null,
                curriculumSourceText: curriculumPayload.curriculumSourceText || null
            });

            typing.remove();

            if (result.conversationId) {
                curriculumPayload.chatConversationId = result.conversationId;
            }
            if (result.reply) {
                appendChatMessage(history, 'assistant', result.reply);
            } else {
                appendChatMessage(
                    history,
                    'assistant',
                    tr('curriculum.chat.empty', null, 'Die KI hat keine Antwort geliefert. Bitte versuche es erneut.')
                );
            }
        } catch (error) {
            typing.remove();
            console.error('curriculum chat failed:', error);
            const errorBubble = appendChatMessage(
                history,
                'assistant',
                error?.message || tr('curriculum.chat.requestFailed', null, 'Antwort konnte nicht erzeugt werden.')
            );
            errorBubble.classList.add('curriculum-chat-message-error');
        } finally {
            isSending = false;
            textarea.disabled = false;
            sendBtn.disabled = false;
            sendBtn.textContent = tr('curriculum.chat.send', null, 'Senden');
            textarea.focus();
        }
    });

    return section;
}

function openKeywordsModal(app, curriculumPayload, triggerButton) {
    closeKeywordsModal();

    const overlay = document.createElement('div');
    overlay.className = 'app-keywords-modal';

    const dialog = document.createElement('div');
    dialog.className = 'app-keywords-modal-content';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', 'appKeywordsTitle');

    const header = document.createElement('div');
    header.className = 'app-keywords-header';

    const titleWrap = document.createElement('div');
    titleWrap.className = 'app-keywords-title-wrap';

    const title = document.createElement('h3');
    title.id = 'appKeywordsTitle';
    title.textContent = tr('curriculum.modalTitle', { app: app.name }, `${app.name} Lehrplan`);

    titleWrap.appendChild(title);

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'app-keywords-close-btn';
    closeButton.textContent = window.i18n?.t ? window.i18n.t('common.close') : 'Close';
    closeButton.onclick = () => closeKeywordsModal();

    header.appendChild(titleWrap);
    header.appendChild(closeButton);

    const body = document.createElement('div');
    body.className = 'app-keywords-body';

    body.appendChild(buildTopicSection(curriculumPayload));
    body.appendChild(buildExplanationSection(curriculumPayload));
    body.appendChild(buildChatSection(curriculumPayload, app));
    body.appendChild(buildStudentContextSection(curriculumPayload));

    dialog.appendChild(header);
    dialog.appendChild(body);
    overlay.appendChild(dialog);
    document.body.appendChild(overlay);

    overlay.addEventListener('click', (event) => {
        if (event.target === overlay) {
            closeKeywordsModal();
        }
    });

    const onKeyDown = (event) => {
        if (event.key === 'Escape') {
            closeKeywordsModal();
        }
    };

    document.addEventListener('keydown', onKeyDown);

    if (triggerButton) {
        triggerButton.classList.add('active');
        triggerButton.setAttribute('aria-expanded', 'true');
    }

    currentKeywordsModal = {
        overlay,
        onKeyDown,
        triggerButton,
        appId: getAppId(app),
        curriculumPayload
    };

    closeButton.focus();
}

function updateKeywordsModalExplanation(curriculumPayload) {
    if (!currentKeywordsModal) return;

    const content = currentKeywordsModal.overlay.querySelector('.curriculum-bridge-summary');
    if (!content) return;

    content.classList.toggle('curriculum-bridge-summary-loading', curriculumPayload.explanationState === 'loading');
    renderExplanationContent(
        content,
        curriculumPayload.explanation,
        curriculumPayload.explanationState === 'loading'
    );
}

async function loadExplanationIntoOpenModal(app, curriculumPayload) {
    if (!currentKeywordsModal || currentKeywordsModal.appId !== getAppId(app)) {
        return;
    }

    if (curriculumPayload.explanationState === 'ready' && curriculumPayload.explanation) {
        return;
    }

    if (!curriculumPayload.topic || !curriculumPayload.topicHasDetail) {
        curriculumPayload.explanation = null;
        curriculumPayload.curriculumSourceText = null;
        curriculumPayload.explanationState = 'unavailable';
        updateKeywordsModalExplanation(curriculumPayload);
        return;
    }

    try {
        const result = await requestKidFriendlyExplanation(
            app,
            curriculumPayload.topic,
            curriculumPayload.educationContext,
            getCurrentLanguage()
        );
        curriculumPayload.explanation = result?.explanation || null;
        curriculumPayload.curriculumSourceText = result?.curriculumSourceText || null;
        curriculumPayload.explanationState = curriculumPayload.explanation ? 'ready' : 'unavailable';
    } catch (error) {
        console.error('Failed to load kid-friendly explanation:', error);
        curriculumPayload.explanation = null;
        curriculumPayload.curriculumSourceText = null;
        curriculumPayload.explanationState = 'unavailable';
    }

    if (!currentKeywordsModal || currentKeywordsModal.appId !== getAppId(app)) {
        return;
    }

    updateKeywordsModalExplanation(curriculumPayload);
}

/**
 * Send a tool call from the AI to the currently open micro-app iframe
 * @param {string} callId - Unique ID for this tool call (from backend)
 * @param {string} toolName - Name of the tool (e.g. "placeTile", "getState")
 * @param {Object} args - Tool arguments
 * @returns {Promise<Object>} - The tool result from the micro-app
 */
export function sendToolCallToApp(callId, toolName, args = {}) {
    return new Promise((resolve, reject) => {
        if (!currentAppIframe || !currentAppIframe.contentWindow) {
            reject(new Error('No micro-app iframe open'));
            return;
        }

        // Store pending call with timeout
        const timeout = setTimeout(() => {
            delete pendingToolCalls[callId];
            reject(new Error(`Tool call ${toolName} timed out after 10s`));
        }, 10000);

        pendingToolCalls[callId] = { resolve, reject, timeout };

        // Send to iframe via postMessage (micro-app expects id + params)
        currentAppIframe.contentWindow.postMessage({
            type: 'tool_call',
            id: callId,
            tool: toolName,
            params: args
        }, '*');

        console.log(`📤 Tool call sent to micro-app: ${toolName}`, args);
    });
}

/**
 * Check if a micro-app is currently open
 * @returns {boolean}
 */
export function isAppOpen() {
    return currentOpenApp !== null && currentAppIframe !== null;
}

/**
 * Get the currently open app object (includes id, name, tools, etc.)
 * @returns {Object|null}
 */
export function getCurrentApp() {
    return currentOpenApp;
}

// Listen for postMessage responses from micro-app iframes
window.addEventListener('message', (event) => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // Tool call result from micro-app (uses "id" field)
    if (data.type === 'tool_result' && data.id) {
        const pending = pendingToolCalls[data.id];
        if (pending) {
            clearTimeout(pending.timeout);
            delete pendingToolCalls[data.id];
            pending.resolve(data.result);
            console.log(`📥 Tool result received from micro-app:`, data.id, data.result);
        }
    }

    // Micro-app can also send unsolicited messages (e.g. state changes)
    if (data.type === 'app_event') {
        console.log(`📥 App event from micro-app:`, data.event, data.payload);
        // Forward to backend via WebSocket if needed
        if (window._appEventHandler) {
            window._appEventHandler(data.event, data.payload);
        }
    }

    // Activity events from micro-apps (analytics/tracking)
    if (data.type === 'activity_event') {
        forwardActivityEvent(data);
    }
});

/**
 * Forward activity events from micro-apps to the backend.
 * Prefers WebSocket (fire-and-forget), falls back to HTTP POST.
 */
function forwardActivityEvent(eventData) {
    const payload = {
        type: 'activity_event',
        app: eventData.app || (currentOpenApp && (currentOpenApp.slug || currentOpenApp.name)) || 'unknown',
        event: eventData.event,
        data: eventData.data || {},
        timestamp: eventData.timestamp || Date.now()
    };

    // WebSocket preferred, HTTP as fallback
    if (window._isWebSocketOpen && window._isWebSocketOpen()) {
        window._sendWebSocketMessage(payload);
    } else {
        const token = getToken();
        if (!token) return;
        fetch(`${CONFIG.API_BASE}/api/activity`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(payload)
        }).catch(() => { /* silent fail — activity events are non-critical */ });
    }
}

/**
 * Initialize the apps grid
 */
export async function initApps() {
    const container = document.getElementById('app-grid-container');
    if (!container) return;

    if (!autoEducationFiltersApplied && !hasActiveCurriculumFilters() && isUserSignedIn()) {
        const seeded = seedFiltersFromCurrentUser();
        if (seeded) {
            currentAppFilters = seeded;
            console.log('[apps] Curriculum-Filter aus Login-Profil übernommen:', seeded);
        }
        autoEducationFiltersApplied = true;
    }

    const { filterPanel, contentPanel } = getAppsShell(container);
    renderCurriculumFilters(filterPanel);
    renderAppsLoading(contentPanel, getFilterUiText().loading);

    try {
        let lang = 'de';
        if (window.i18n && typeof window.i18n.getCurrentLanguage === 'function') {
            lang = window.i18n.getCurrentLanguage();
        } else {
            lang = localStorage.getItem('appLanguage') || 'de';
        }

        const token = getToken();
        const headers = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }

        currentPage = 1;
        const response = await fetch(buildProjectRequestUrl(lang, currentAppFilters, currentPage, APPS_PER_PAGE).toString(), {
            headers
        });

        if (!response.ok) {
            // Check for auth errors (401/429)
            const authResult = await handleAuthError(response);
            if (authResult.handled) {
                renderAppsError(contentPanel, authResult.error);
                return;
            }
            throw new Error('Failed to fetch apps');
        }

        const rawData = await response.json();
        const rawApps = Array.isArray(rawData) ? rawData : (rawData.items ?? rawData.apps ?? rawData.data ?? []);
        currentTotal = Array.isArray(rawData) ? rawApps.length : (rawData.total ?? rawApps.length);
        allApps = transformAppUrls(rawApps);

        try {
            if (isUserSignedIn()) {
                const subjects = await requestSubjectsLookup(lang, {
                    countryId: currentAppFilters.countryId,
                    regionId: currentAppFilters.regionId,
                    schoolTypeId: currentAppFilters.schoolTypeId,
                    gradeId: currentAppFilters.gradeId
                });
                currentCurriculumLookups = { ...createEmptyCurriculumLookups(), subjects };
            } else {
                currentCurriculumLookups = await requestCurriculumLookups(lang, currentAppFilters);
            }
        } catch (lookupError) {
            console.error('Failed to load curriculum lookups:', lookupError);
            currentCurriculumLookups = createEmptyCurriculumLookups();
            showToast(lookupError.message || 'Failed to load curriculum lookups.', 'error');
        }

        renderCurriculumFilters(filterPanel);
        const searchInput = document.getElementById('appSearchInput');
        const activeQuery = String(searchInput?.value || '').trim().toLowerCase();
        if (activeQuery) {
            filterApps(activeQuery);
        } else {
            renderAppGrid(contentPanel, allApps);
            renderPagination(contentPanel);
        }

        setupSearch();
    } catch (error) {
        console.error('Error loading apps:', error);
        renderAppsError(contentPanel, getFilterUiText().failedLoad);
        showToast('Failed to load applications', 'error');
    }
}

function renderPagination(container) {
    const totalPages = Math.ceil(currentTotal / APPS_PER_PAGE);
    if (totalPages <= 1) return;

    const hasPrev = currentPage > 1;
    const hasNext = currentPage < totalPages;

    const nav = document.createElement('nav');
    nav.className = 'apps-pagination';

    const pageButtons = Array.from({ length: totalPages }, (_, i) => {
        const p = i + 1;
        return `<button class="apps-pagination-btn apps-pagination-page${p === currentPage ? ' active' : ''}" data-page="${p}">${p}</button>`;
    }).join('');

    nav.innerHTML = `
        <button class="apps-pagination-btn" data-page="${currentPage - 1}" ${hasPrev ? '' : 'disabled'}>&lsaquo;</button>
        ${pageButtons}
        <button class="apps-pagination-btn" data-page="${currentPage + 1}" ${hasNext ? '' : 'disabled'}>&rsaquo;</button>
    `;

    nav.querySelectorAll('.apps-pagination-btn:not([disabled]):not(.active)').forEach((btn) => {
        btn.addEventListener('click', () => loadPage(Number(btn.dataset.page)));
    });

    container.appendChild(nav);
}

async function loadPage(page) {
    const container = document.getElementById('app-grid-content') || document.getElementById('app-grid-container');
    if (!container) return;

    currentPage = page;
    renderAppsLoading(container, getFilterUiText().loading);

    try {
        let lang = 'de';
        if (window.i18n && typeof window.i18n.getCurrentLanguage === 'function') {
            lang = window.i18n.getCurrentLanguage();
        } else {
            lang = localStorage.getItem('appLanguage') || 'de';
        }

        const token = getToken();
        const headers = {};
        if (token) headers['Authorization'] = `Bearer ${token}`;

        const response = await fetch(buildProjectRequestUrl(lang, currentAppFilters, currentPage, APPS_PER_PAGE).toString(), { headers });

        if (!response.ok) {
            const authResult = await handleAuthError(response);
            if (authResult.handled) {
                renderAppsError(container, authResult.error);
                return;
            }
            throw new Error('Failed to fetch apps');
        }

        const rawData = await response.json();
        const rawApps = Array.isArray(rawData) ? rawData : (rawData.items ?? rawData.apps ?? rawData.data ?? []);
        currentTotal = Array.isArray(rawData) ? rawApps.length : (rawData.total ?? rawApps.length);
        allApps = transformAppUrls(rawApps);

        renderAppGrid(container, allApps);
        renderPagination(container);
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (error) {
        console.error('Error loading page:', error);
        renderAppsError(container, getFilterUiText().failedLoad);
    }
}

// Listen for language changes if not listener attached
if (!window.appsLanguageListenerAttached) {
    window.addEventListener('languageChanged', () => {
        initApps();
    });
    window.appsLanguageListenerAttached = true;
}

/**
 * Setup search functionality
 */
function setupSearch() {
    if (window.searchListenerAttached) return;

    document.addEventListener('input', (e) => {
        if (e.target && e.target.id === 'appSearchInput') {
            const query = e.target.value.toLowerCase();
            filterApps(query);
        }
    });

    window.searchListenerAttached = true;
}

/**
 * Filter apps based on query
 * @param {string} query 
 */
function filterApps(query) {
    const container = document.getElementById('app-grid-content') || document.getElementById('app-grid-container');
    if (!container || !allApps) return;

    if (!query) {
        renderAppGrid(container, allApps);
        return;
    }

    const filtered = allApps.filter(app =>
        (app.name && app.name.toLowerCase().includes(query)) ||
        (app.description && app.description.toLowerCase().includes(query))
    );

    renderAppGrid(container, filtered);
}

/**
 * Render the apps grid
 * @param {HTMLElement} container 
 * @param {Array} apps 
 */
function renderAppGrid(container, apps) {
    if (!apps || apps.length === 0) {
        const text = getFilterUiText();
        container.innerHTML = `
            <div style="text-align: center; padding: 4rem; color: var(--text-secondary);">
                <p>${escapeHtml(hasActiveCurriculumFilters() ? text.noAppsFiltered : text.noApps)}</p>
            </div>
        `;
        return;
    }

    // Setup theme observer if not already active
    setupThemeObserver();

    container.innerHTML = '<div id="app-grid"></div>';
    const grid = document.getElementById('app-grid');

    apps.forEach(app => {
        const card = document.createElement('div');
        card.className = 'app-card';

        // Check current theme state
        const isDark = isDarkMode();

        const screenshotLight = app.screenshotUrlLight || app.screenshotUrl;
        const screenshotDark = app.screenshotUrlDark || app.screenshotUrl;

        // Determine initial screenshot
        const currentScreenshot = isDark ? screenshotDark : screenshotLight;

        card.innerHTML = `
            <div class="app-card-preview">
                ${currentScreenshot ? `
                    <img src="${currentScreenshot}" 
                         data-light-src="${screenshotLight}" 
                         data-dark-src="${screenshotDark}" 
                         alt="${app.name}" 
                         class="app-card-image" 
                         loading="lazy">
                ` : `<div class="app-card-placeholder">No Preview</div>`}
                <div class="play-overlay">
                    <div class="play-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
                    </div>
                </div>
            </div>
            <div class="app-card-content">
                <div class="app-card-title" title="${app.name}">${app.name}</div>
                <div class="app-card-desc" title="${app.description || ''}">${app.description || 'No description available'}</div>
            </div>
        `;

        // Add click handler
        card.onclick = () => openApp(app);

        // Append to grid
        grid.appendChild(card);
    });
}

/**
 * Check if dark mode is active
 * @returns {boolean}
 */
function isDarkMode() {
    return document.body.classList.contains('dark-mode') ||
        document.documentElement.classList.contains('dark-mode') ||
        document.documentElement.getAttribute('data-theme') === 'dark' ||
        localStorage.getItem('appTheme') === 'dark';
}

let themeObserver = null;

/**
 * Setup observer to detect theme changes and update app images
 */
function setupThemeObserver() {
    if (themeObserver) return;

    themeObserver = new MutationObserver(() => {
        updateAppImages();
    });

    themeObserver.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    themeObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'data-theme'] });
}

/**
 * Update all app images based on current theme
 */
function updateAppImages() {
    const isDark = isDarkMode();
    const images = document.querySelectorAll('.app-card-image');

    images.forEach(img => {
        const newSrc = isDark ? img.dataset.darkSrc : img.dataset.lightSrc;
        if (newSrc && img.getAttribute('src') !== newSrc) {
            img.src = newSrc;
        }
    });
}

/**
 * Open an app in an iframe
 * @param {Object} app 
 */
function openApp(app) {
    if (!app.url) {
        showToast('App has no URL', 'error');
        return;
    }

    // Get current theme and language
    const isDark = isDarkMode();
    const theme = isDark ? 'dark' : 'light';

    // Get language from i18n or fall back to localStorage
    const lang = getCurrentLanguage();

    const llmEnabledForApp = isUserSignedIn();
    const buildAppUrl = () => {
        const url = new URL(app.url);
        url.searchParams.set('lang', lang);
        url.searchParams.set('theme', theme);

        if (llmEnabledForApp) {
            url.searchParams.set('llm', 'on');
        } else {
            url.searchParams.delete('llm');
        }

        return url.toString();
    };

    // --- Header Modification Logic ---
    const headerTitle = document.getElementById('headerTitle');
    const headerRight = document.querySelector('.header-right');
    const settingsIcon = document.getElementById('settingsIconContainer');

    // Store original state only if elements exist
    const originalTitle = headerTitle ? headerTitle.textContent : null;
    const originalTitleSize = headerTitle ? headerTitle.style.fontSize : null;

    // Update Title if element exists
    if (headerTitle) {
        headerTitle.textContent = app.name;
        headerTitle.style.display = 'block'; // Show title when app is open
    } else {
        console.warn('headerTitle element not found - header title will not update');
    }

    hideHeaderSubjectFilter();

    // Create Close Button for Header
    const closeBtn = document.createElement('button');
    closeBtn.className = 'close-iframe-btn'; // Reuse same class for styling
    closeBtn.title = 'App schließen';
    closeBtn.style.marginRight = '10px';
    closeBtn.innerHTML = `
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M18 6L6 18M6 6l12 12"/>
        </svg>
    `;

    // Curriculum-Button nur fuer eingeloggte Schueler erzeugen.
    let curriculumBtn = null;
    if (isUserSignedIn()) {
        curriculumBtn = document.createElement('button');
        curriculumBtn.className = 'curriculum-toggle-btn';
        curriculumBtn.type = 'button';
        curriculumBtn.title = tr('curriculum.buttonTitle', null, 'Lehrplan-Inhalte anzeigen');
        curriculumBtn.setAttribute('aria-expanded', 'false');
        curriculumBtn.innerHTML = `
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"></path>
                <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 0 4 24V4.5A2.5 2.5 0 0 1 6.5 2z"></path>
            </svg>
            <span>${escapeHtml(tr('curriculum.buttonLabel', null, 'Lehrplan'))}</span>
        `;
    }

    // Insert Close Button before other items in header-right
    if (headerRight) {
        if (curriculumBtn) {
            headerRight.insertBefore(curriculumBtn, headerRight.firstChild);
        }
        headerRight.insertBefore(closeBtn, headerRight.firstChild);
    }

    // Optional: Hide Settings Icon to reduce clutter
    if (settingsIcon) {
        settingsIcon.style.display = 'none';
    }

    // --- Container ---
    const container = document.createElement('div');
    container.className = 'app-iframe-container';
    // Note: No toolbar in the container anymore
    container.innerHTML = `
        <div class="iframe-loading" style="position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; background: var(--bg-primary, white); z-index: -1;">
            <div class="loading-spinner"></div>
        </div>
        <iframe src="${buildAppUrl()}" class="app-iframe" allow="microphone; camera; geolocation; accelerometer; gyroscope; magnetometer; midi; payment; usb; vr; xr-spatial-tracking" sandbox="allow-forms allow-modals allow-popups allow-presentation allow-same-origin allow-scripts" onload="this.style.opacity='1'"></iframe>
    `;

    document.body.appendChild(container);

    // Track current app and iframe for tool call bridge
    currentOpenApp = app;
    currentAppIframe = container.querySelector('.app-iframe');

    if (curriculumBtn) {
        curriculumBtn.onclick = async () => {
            if (!isUserSignedIn()) {
                showToast(tr('curriculum.errors.signInRequired', null, 'Bitte einloggen, um den Lehrplan zu sehen.'), 'error');
                return;
            }

            const appId = getAppId(app);
            if (!appId) {
                showToast(tr('curriculum.errors.appIdMissing', null, 'App ID missing.'), 'error');
                return;
            }

            if (currentKeywordsModal?.appId === appId) {
                closeKeywordsModal();
                return;
            }

            curriculumBtn.disabled = true;

            try {
                const curriculumPayload = await prepareCurriculumPayload(app);
                openKeywordsModal(app, curriculumPayload, curriculumBtn);
                loadExplanationIntoOpenModal(app, curriculumPayload);
            } catch (error) {
                console.error('Failed to load curriculum topic:', error);
                showToast(error.message || tr('curriculum.errors.loadFailed', null, 'Lehrplan-Daten konnten nicht geladen werden.'), 'error');
                curriculumBtn.classList.remove('active');
                curriculumBtn.setAttribute('aria-expanded', 'false');
            } finally {
                curriculumBtn.disabled = false;
            }
        };
    }

    // Notify backend that an app was opened (sends app context for AI tools)
    notifyAppOpened(app);

    // Close Handler
    const closeApp = () => {
        closeKeywordsModal();

        // Clear tool call bridge state first so getCurrentApp() returns null
        currentOpenApp = null;
        currentAppIframe = null;

        // Notify backend with app_id=null
        notifyAppClosed();
        // Reject any pending tool calls
        Object.keys(pendingToolCalls).forEach(callId => {
            clearTimeout(pendingToolCalls[callId].timeout);
            pendingToolCalls[callId].reject(new Error('App closed'));
            delete pendingToolCalls[callId];
        });

        // Animate out
        const animation = container.animate([
            { opacity: 1 },
            { opacity: 0 }
        ], {
            duration: 200,
            fill: 'forwards'
        });

        animation.onfinish = () => {
            container.remove();

            // Restore Header
            if (headerTitle) {
                headerTitle.textContent = originalTitle;
                headerTitle.style.display = 'none'; // Hide title when no app is open
            }
            if (curriculumBtn) curriculumBtn.remove();
            if (closeBtn) closeBtn.remove();
            if (settingsIcon) settingsIcon.style.display = ''; // Restore default display

            if (isUserSignedIn()) {
                renderSubjectOnlyFilter();
            }
        };
    };

    closeBtn.onclick = closeApp;
}

/**
 * Notify the backend that a micro-app was opened by re-sending the UI config
 * with the current app_id. The backend loads tool definitions + system prompt from DB.
 * @param {Object} app - The app object
 */
function notifyAppOpened(app) {
    // Re-send UI config - it now includes app_id from getCurrentApp()
    if (window.sendUIConfig) {
        window.sendUIConfig();
        console.log(`📱 Sent config with app_id: ${app.name} (${getAppId(app)})`);
    }
}

/**
 * Notify the backend that the micro-app was closed by re-sending UI config
 * with app_id=null. The backend removes app-specific tools and system prompt.
 */
function notifyAppClosed() {
    // currentOpenApp is already set to null before this is called
    // Re-send UI config - getCurrentApp() will return null → app_id: null
    if (window.sendUIConfig) {
        window.sendUIConfig();
        console.log('📱 Sent config with app_id: null (app closed)');
    }
}

/**
 * Transform app URLs to use the new S3 bucket domain
 * @param {Array} apps 
 * @returns {Array}
 */
function transformAppUrls(apps) {
    const TARGET_BASE = "https://cyberschoolingbucket.s3.pl-waw.scw.cloud";
    const PATTERNS_TO_REPLACE = [
        "https://s3.pl-waw.scw.cloud/cyberschoolingbucket",
        "https://s3.tebi.io/cyberschoolingbucket"
    ];

    return apps.map(app => {
        // Create a shallow copy to avoid mutating the original object if it matters
        const newApp = { ...app };
        const fields = ['url', 'mainUrl', 'lastDeploymentUrl', 'screenshotUrl', 'screenshotUrlLight', 'screenshotUrlDark'];

        fields.forEach(field => {
            if (newApp[field]) {
                PATTERNS_TO_REPLACE.forEach(pattern => {
                    if (newApp[field].startsWith(pattern)) {
                        newApp[field] = newApp[field].replace(pattern, TARGET_BASE);
                    }
                });
            }
        });
        return newApp;
    });
}
